import classNames from "classnames";
import React, { useCallback, useMemo } from "react";
import { AccordionSection, AccordionSectionHeaderRendererProps } from "../accordion";
import { IconNavArrowDown, IconNavArrowUp } from "../icons";
import { Text } from "../text";
import { NavMenuItem, NavSubMenuItem } from "../types";
import { CSS_CLASSES as NAV_MENU_CSS_CLASSES } from "./nav-menu-css-classes";
import { VerticalMainNavMenuSubItem } from "./vertical-main-nav-menu-sub-item";

export interface VerticalMainNavMenuItemProps extends NavMenuItem {
  color?: "interactive" | "primary";
  onSubMenuItemActive: (subMenuItem: NavSubMenuItem) => void;
  restItemProperties: unknown;
}

export const VerticalMainNavMenuItem = ({
  id,
  label,
  href,
  className,
  navMenuItemComponent,
  navMenuItemComponentProps,
  subMenuItems,
  color = "interactive",
  onSubMenuItemActive,
  restItemProperties,
  ...restProps
}: VerticalMainNavMenuItemProps) => {
  const subMenuItemsClassName = useMemo(
    () =>
      classNames(NAV_MENU_CSS_CLASSES.VERTICAL_MAIN_NAV_MENU_SUB_ITEMS, {
        [NAV_MENU_CSS_CLASSES.VERTICAL_MAIN_NAV_MENU_SUB_ITEMS_COLOR_INTERACTIVE]: color === "interactive",
        [NAV_MENU_CSS_CLASSES.VERTICAL_MAIN_NAV_MENU_SUB_ITEMS_COLOR_PRIMARY]: color === "primary",
      }),
    [color]
  );

  return (
    <AccordionSection
      key={id}
      label={label}
      color={color}
      headerRenderer={getVerticalMainNavMenuItemRenderer(
        href,
        className,
        navMenuItemComponent,
        navMenuItemComponentProps,
        subMenuItems,
        restItemProperties
      )}
      {...restProps}
    >
      {subMenuItems.length > 0 ? (
        <div className={subMenuItemsClassName}>
          {subMenuItems.map((subItem) => (
            <VerticalMainNavMenuSubItem key={subItem.id} subItem={subItem} onClick={onSubMenuItemActive} />
          ))}
        </div>
      ) : undefined}
    </AccordionSection>
  );
};

const getVerticalMainNavMenuItemRenderer =
  (
    href?: string,
    className?: string,
    navMenuItemComponent?: React.ComponentType<any>,
    navMenuItemComponentProps?: any,
    subMenuItems: NavSubMenuItem[] = [],
    restItemProperties?: any
  ) =>
  ({ id, label, color, index, active, onAccordionSectionClick, ariaProps }: AccordionSectionHeaderRendererProps) => {
    const menuItemClassName = useMemo(
      () =>
        classNames(NAV_MENU_CSS_CLASSES.VERTICAL_MAIN_NAV_MENU_ITEM_ROOT, {
          [NAV_MENU_CSS_CLASSES.VERTICAL_MAIN_NAV_MENU_ITEM_COLOR_INTERACTIVE]: color === "interactive",
          [NAV_MENU_CSS_CLASSES.VERTICAL_MAIN_NAV_MENU_ITEM_COLOR_PRIMARY]: color === "primary",
        }),
      [color]
    );

    const labelClassName = useMemo(
      () =>
        classNames(
          NAV_MENU_CSS_CLASSES.VERTICAL_MAIN_NAV_MENU_ITEM_LABEL,
          {
            [NAV_MENU_CSS_CLASSES.VERTICAL_MAIN_NAV_MENU_ITEM_LABEL_ACTIVE]: active,
          },
          className
        ),
      [active]
    );

    const Icon = useMemo(() => (active ? IconNavArrowUp : IconNavArrowDown), [active]);

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        onAccordionSectionClick(event, index, active);
      },
      [active, index, onAccordionSectionClick]
    );

    const Component = navMenuItemComponent ? navMenuItemComponent : href ? "a" : "button";

    return (
      <Component
        id={id}
        onClick={handleClick}
        href={href}
        className={menuItemClassName}
        {...ariaProps}
        {...navMenuItemComponentProps}
        {...restItemProperties}
      >
        <Text color="currentcolor" className={labelClassName}>
          {label}
        </Text>
        {subMenuItems?.length > 0 && <Icon className={NAV_MENU_CSS_CLASSES.VERTICAL_MAIN_NAV_MENU_ITEM_LABEL_ICON} />}
      </Component>
    );
  };
